import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { preInscricoesApi } from '../../lib/api';
import logger from '../../lib/logger';
import { Search, Download, Eye, RefreshCw, Copy, ClipboardList } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { PreInscricoesStats, StatusBadge, STATUS_LABELS } from '../../components/admin/PreInscricoesStats';
import { PreInscricaoDetailsDialog } from '../../components/admin/PreInscricaoDetailsDialog';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '-');
const fmtPhone = (d = '') => (d.length === 11 ? `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}` : d.length === 10 ? `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}` : d);

const PreInscricoesPage = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [dupes, setDupes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [showDupes, setShowDupes] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s, d] = await Promise.all([
        preInscricoesApi.list({ busca: busca || undefined, status: status || undefined }),
        preInscricoesApi.stats(),
        preInscricoesApi.duplicados(),
      ]);
      setItems(l.data.items);
      setStats(s.data);
      setDupes(d.data);
    } catch (e) {
      logger.error(e);
      toast.error('Erro ao carregar pré-inscrições');
    } finally { setLoading(false); }
  }, [busca, status]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const dupeKeys = new Set((dupes?.grupos || []).flatMap((g) => g.registros.map((r) => r.pre_inscricao_id)));

  const onChanged = (updated) => {
    setItems((prev) => prev.map((i) => (i.pre_inscricao_id === updated.pre_inscricao_id ? updated : i)));
    setSelected(updated);
    preInscricoesApi.stats().then((s) => setStats(s.data)).catch(() => {});
  };
  const onDeleted = (id) => { setItems((prev) => prev.filter((i) => i.pre_inscricao_id !== id)); load(); };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="pre-inscricoes-page">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl text-white flex items-center gap-3"><ClipboardList className="w-7 h-7 text-kairos-orange" />Pré-inscrições Kairós 2027</h1>
            <p className="text-gray-400 mt-1">Casais interessados na próxima edição. Pré-inscrição ≠ inscrição paga.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} className="border-white/10 text-gray-300 hover:bg-white/5" data-testid="refresh-btn"><RefreshCw className="w-4 h-4 mr-2" />Atualizar</Button>
            <a href={preInscricoesApi.exportCsvUrl({ ...(status ? { status } : {}) })} data-testid="export-csv-btn">
              <Button className="bg-kairos-orange hover:bg-kairos-orange-dark text-white"><Download className="w-4 h-4 mr-2" />Exportar CSV</Button>
            </a>
          </div>
        </div>

        <PreInscricoesStats stats={stats} />

        {dupes?.total_grupos > 0 && (
          <Card className="bg-yellow-500/5 border-yellow-500/30">
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <p className="text-yellow-300 text-sm flex items-center gap-2"><Copy className="w-4 h-4" />{dupes.total_grupos} grupo(s) de cadastros duplicados (mesmo e-mail/telefone).</p>
              <Button variant="ghost" size="sm" onClick={() => setShowDupes(!showDupes)} className="text-yellow-300 hover:bg-yellow-500/10" data-testid="toggle-dupes-btn">{showDupes ? 'Ocultar' : 'Ver'}</Button>
            </CardContent>
            {showDupes && (
              <CardContent className="pt-0 pb-4 px-4 text-xs text-gray-300 space-y-1" data-testid="dupes-list">
                {dupes.grupos.map((g, i) => <p key={i}><span className="text-gray-500">{g.tipo}:</span> {g.chave} — {g.registros.map((r) => r.nome_participante_1).join(', ')}</p>)}
              </CardContent>
            )}
          </Card>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por nome, e-mail, WhatsApp ou cidade..." className="pl-10 bg-obsidian-200 border-white/10 text-white" data-testid="search-input" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md bg-obsidian-200 border border-white/10 text-white px-3 text-sm" data-testid="status-filter">
            <option value="">Todos os status</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        <Card className="bg-obsidian-200 border-white/10">
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="p-12 flex justify-center"><div className="w-10 h-10 border-4 border-kairos-orange border-t-transparent rounded-full animate-spin" /></div>
            ) : items.length === 0 ? (
              <p className="p-12 text-center text-gray-500" data-testid="empty-state">Nenhuma pré-inscrição encontrada.</p>
            ) : (
              <table className="admin-table w-full text-sm" data-testid="pre-inscricoes-table">
                <thead>
                  <tr className="text-left text-gray-500 text-xs uppercase border-b border-white/5">
                    <th className="p-4">Casal</th><th className="p-4">Cidade/UF</th><th className="p-4">WhatsApp</th><th className="p-4">E-mail</th><th className="p-4">Data</th><th className="p-4">Origem</th><th className="p-4">Status</th><th className="p-4 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.pre_inscricao_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`pi-row-${it.pre_inscricao_id}`}>
                      <td className="p-4">
                        <p className="text-white font-medium">{it.nome_participante_1}</p>
                        <p className="text-gray-500">{it.nome_participante_2}</p>
                        {dupeKeys.has(it.pre_inscricao_id) && <span className="text-[10px] text-yellow-300">possível duplicado</span>}
                      </td>
                      <td className="p-4 text-gray-400">{it.cidade}/{it.estado}</td>
                      <td className="p-4 text-gray-400 whitespace-nowrap">{fmtPhone(it.telefone)}</td>
                      <td className="p-4 text-gray-400">{it.email}</td>
                      <td className="p-4 text-gray-400 whitespace-nowrap">{fmtDate(it.criado_em)}</td>
                      <td className="p-4 text-gray-400">{it.utm?.utm_source || it.origem || 'site'}</td>
                      <td className="p-4"><StatusBadge status={it.status} /></td>
                      <td className="p-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => { setSelected(it); setOpen(true); }} className="text-gray-400 hover:text-white" data-testid={`view-pi-${it.pre_inscricao_id}`}><Eye className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <PreInscricaoDetailsDialog item={selected} open={open} onOpenChange={setOpen} onChanged={onChanged} onDeleted={onDeleted} />
      </div>
    </AdminLayout>
  );
};

export default PreInscricoesPage;
